import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Typography, Alert, IconButton, Chip, Box,
} from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientVerticalsApi } from '../api/clients.api';
import { verticalsApi } from '../api/verticals.api';
import { extractApiError } from '../api/error';

interface Props {
  open: boolean;
  onClose: () => void;
  clientId: number | null;
  clientName?: string;
}

/**
 * Admin panel to map a client to one or more business verticals (services) — PDI / Asset
 * Management / AMC / EPC / R&M. A client may belong to several services at once. Only LIVE
 * (active) verticals can be assigned.
 */
export function ClientVerticalsDialog({ open, onClose, clientId, clientName }: Props) {
  const qc = useQueryClient();
  const [verticalId, setVerticalId] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);
  const key = ['client-verticals', clientId];

  const { data: assigned, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => clientVerticalsApi.list(clientId as number),
    enabled: open && clientId != null,
  });
  const { data: allVerticals } = useQuery({
    queryKey: ['verticals-accessible'],
    queryFn: () => verticalsApi.listAccessible(),
    enabled: open,
  });

  const assignedIds = new Set((assigned ?? []).map((v) => v.verticalId));
  const available = (allVerticals ?? []).filter((v) => !assignedIds.has(v.id));

  const assignMut = useMutation({
    mutationFn: () => clientVerticalsApi.assign(clientId as number, verticalId as number),
    onSuccess: (rows) => { qc.setQueryData(key, rows); setVerticalId(''); setError(null); },
    onError: (e) => setError(extractApiError(e)),
  });
  const revokeMut = useMutation({
    mutationFn: (vId: number) => clientVerticalsApi.revoke(clientId as number, vId),
    onSuccess: (rows) => { qc.setQueryData(key, rows); setError(null); },
    onError: (e) => setError(extractApiError(e)),
  });

  const close = () => { setError(null); setVerticalId(''); onClose(); };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>
        Services / Verticals — {clientName ?? 'client'}
        <IconButton onClick={close} sx={{ position: 'absolute', right: 8, top: 8 }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Map this client to the business services it works with. A client may belong to several
            at once (e.g. PDI + Asset Management). Only live services can be assigned.
          </Alert>

          <Box>
            <Typography variant="subtitle2" gutterBottom>Mapped services</Typography>
            {isLoading && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
            {!isLoading && (assigned?.length ?? 0) === 0 && <Typography variant="body2" color="text.secondary">No services mapped yet.</Typography>}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {assigned?.map((v) => (
                <Chip key={v.id} label={v.name} color="primary" variant="outlined"
                  onDelete={() => revokeMut.mutate(v.verticalId)} disabled={revokeMut.isPending} />
              ))}
            </Stack>
          </Box>

          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <Typography variant="subtitle2">Add a service</Typography>
          <Stack direction="row" spacing={1}>
            <TextField select size="small" label="Service" value={verticalId} sx={{ flex: 1 }}
              onChange={(e) => setVerticalId(e.target.value === '' ? '' : Number(e.target.value))}>
              {available.length === 0 && <MenuItem value="" disabled>All live services already mapped</MenuItem>}
              {available.map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
            </TextField>
            <Button variant="contained" startIcon={<Add />} disabled={verticalId === '' || assignMut.isPending} onClick={() => assignMut.mutate()}>Add</Button>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
